'use client';

import { ApiClientProvider } from '@cldsystems/ws-api-client';
import axios, { type AxiosInstance } from 'axios';
import { createContext, FC, PropsWithChildren, useContext, useEffect, useRef } from 'react';
import { authInterceptorWithGetter } from '@/services/auth-interceptor';
import { setupAxiosCorsErrorHandler } from '@/services/setup-cors-error-handler';
import { useStudioKeycloak } from './studio-keycloak-provider';

const studioHttpClient: AxiosInstance = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_BASE_URL,
});

const StudioHttpClientCtx = createContext<AxiosInstance | null>(null);

export const useStudioHttpClient = (): AxiosInstance => {
  const client = useContext(StudioHttpClientCtx);
  if (!client) throw new Error('useStudioHttpClient must be used within StudioProvider');
  return client;
};

export const StudioApiProvider: FC<PropsWithChildren> = ({ children }) => {
  const { keycloak } = useStudioKeycloak();
  const interceptorIdRef = useRef<number | null>(null);

  useEffect(() => {
    setupAxiosCorsErrorHandler(studioHttpClient);
  }, []);

  useEffect(() => {
    if (interceptorIdRef.current != null) {
      studioHttpClient.interceptors.request.eject(interceptorIdRef.current);
      interceptorIdRef.current = null;
    }
    if (keycloak) {
      interceptorIdRef.current = authInterceptorWithGetter(
        studioHttpClient,
        () => keycloak.token ?? undefined,
      );
    }
    return () => {
      if (interceptorIdRef.current != null) {
        studioHttpClient.interceptors.request.eject(interceptorIdRef.current);
        interceptorIdRef.current = null;
      }
    };
  }, [keycloak]);

  return (
    <StudioHttpClientCtx.Provider value={studioHttpClient}>
      <ApiClientProvider httpClient={studioHttpClient} hasSchema={false}>
        {children}
      </ApiClientProvider>
    </StudioHttpClientCtx.Provider>
  );
};
